<template>
  <div class="search_sidebar" :class="{ no_results: !search || !resultCount }">
    <div class="input_container">
      <div v-show="search" class="right_icon" @click="search = null">
        <svg-icon
          v-if="searching"
          class="loading"
          :width="'14'"
          :height="'14'"
          icon="app-icons/spinner"
          fill="#fff"
        />
        <svg-icon
          v-else
          ref="icon"
          class="clear"
          :width="'12'"
          :height="'12'"
          icon="app-icons/close"
          fill="#fff"
        />
      </div>
      <input
        ref="searchInput"
        v-model.trim="search"
        class="search_input"
        placeholder="Search..."
        :spellcheck="false"
      />
    </div>
    <div v-if="search && !searching && !resultCount" class="results_msg">
      No results found.
    </div>
    <div v-if="search" class="results">
      <!-- BOARDS -->
      <div v-if="filteredBoards.length" class="header">Boards</div>
      <div v-for="board in filteredBoards" :key="board.id">
        <div class="item" @click="onClickBoard(board)">
          <div class="icon">
            <svg-icon
              ref="icon"
              :width="'16'"
              :height="'16'"
              fill="#bcbcbc"
              icon="app-icons/board"
            />
          </div>
          <div class="details">
            <div class="name" v-html="boardTitle(board)" />
          </div>
        </div>
      </div>
      <!-- ELEMENTS -->
      <div v-if="filteredElements.length" class="header">Elements</div>
      <vddl-draggable
        v-for="element in filteredElements"
        :key="element.id"
        :draggable="element"
        :index="element.index"
        :wrapper="filteredElements"
        :dragstart="onElementDragStart"
        :dragend="onDragEnd"
        :moved="onMove"
        :canceled="onCancel"
        :type="elementType"
        :data-id="element.id"
      >
        <div
          class="item"
          :draggable="canEdit"
          :data-id="element.id"
          @dragstart="onStartDrag($event)"
          @click="onClickElement(element)"
        >
          <div class="icon">
            <svg-icon
              ref="icon"
              :width="'16'"
              :height="'16'"
              fill="#bcbcbc"
              icon="app-icons/element"
              @on-load="onElementIconLoad"
            />
          </div>
          <div class="details">
            <div class="name" v-html="elementTitle(element)" />
            <div
              v-if="elementContent(element)"
              class="content"
              v-html="elementContent(element)"
            />
            <div v-if="elementParent(element)" class="parent">
              {{ elementParent(element).name }}
            </div>
          </div>
        </div>
      </vddl-draggable>
      <!-- JUMPERS -->
      <div v-if="filteredJumpers.length" class="header">Jumpers</div>
      <div v-for="jumper in filteredJumpers" :key="jumper.id">
        <div class="item" @click="onClickJumper(jumper)">
          <div class="icon">
            <svg-icon
              ref="icon"
              :width="'16'"
              :height="'16'"
              fill="#bcbcbc"
              icon="app-icons/jumper"
            />
          </div>
          <div class="details">
            <div class="name" v-html="jumperTitle(jumper)" />
            <div class="parent">
              {{ jumperParent(jumper).name }}
            </div>
          </div>
        </div>
      </div>
      <!-- NOTES -->
      <div v-if="filteredNotes.length" class="header">Notes</div>
      <div v-for="note in filteredNotes" :key="note.id">
        <div class="item" @click="onClickNote(note)">
          <div class="icon">
            <svg-icon
              ref="icon"
              :width="'16'"
              :height="'16'"
              fill="#bcbcbc"
              icon="app-icons/note"
            />
          </div>
          <div class="details">
            <div class="content" v-html="noteContent(note)" />
            <div class="parent">
              {{ noteParent(note).name }}
            </div>
          </div>
        </div>
      </div>
      <!-- BRANCHES -->
      <div v-if="filteredBranches.length" class="header">Branches</div>
      <div v-for="branch in filteredBranches" :key="branch.id">
        <div class="item" @click="onClickBranch(branch)">
          <div class="icon">
            <svg-icon
              ref="icon"
              :width="'16'"
              :height="'16'"
              fill="#bcbcbc"
              icon="app-icons/branch"
            />
          </div>
          <div class="details">
            <div class="content" v-html="branchContent(branch)" />
            <div class="parent">
              {{ branchParent(branch).name }}
            </div>
          </div>
        </div>
      </div>
      <!-- CONNECTIONS -->
      <div v-if="filteredConnections.length" class="header">Connections</div>
      <div v-for="connection in filteredConnections" :key="connection.id">
        <div class="item" @click="onClickConnection(connection)">
          <div class="icon">
            <svg-icon
              ref="icon"
              :width="'16'"
              :height="'16'"
              fill="#bcbcbc"
              icon="app-icons/connection"
            />
          </div>
          <div class="details">
            <div class="name" v-html="connectionLabel(connection)" />
            <div class="parent">
              {{ connectionParent(connection).name }}
            </div>
          </div>
        </div>
      </div>
      <!-- COMPONENTS -->
      <div v-if="filteredComponents.length" class="header">Components</div>

      <vddl-draggable
        v-for="component in filteredComponents"
        :key="component.id"
        :draggable="component"
        :index="component.index"
        :wrapper="filteredComponents"
        :dragstart="onComponentDragStart"
        :dragend="onDragEnd"
        :moved="onComponentMove"
        :canceled="onCancel"
        :type="componentType"
        :data-id="component.id"
      >
        <div
          class="item"
          :draggable="canEdit"
          :data-id="component.id"
          @click="onClickComponent(component)"
          @dragstart="onComponentStartDrag($event)"
        >
          <div
            class="thumb"
            :class="{ hasimage: componentImageUrl(component) }"
          >
            <img
              v-if="componentImageUrl(component)"
              width="22"
              height="22"
              :src="componentImageUrl(component)"
              @load="
                onComponentImageLoad(componentImageUrl(component), component.id)
              "
            />
            <svg-icon
              v-else-if="icon(component)"
              ref="icon"
              :icon="icon(component)"
              :width="'22'"
              :height="'22'"
              @on-load="onComponentIconLoad($event, component.id)"
            />
          </div>
          <div class="details">
            <div class="name" v-html="componentName(component)" />
            <div class="content" v-html="componentContent(component)" />
            <div
              v-if="componentParent(component) !== getRootContainer"
              class="parent"
            >
              {{ componentParent(component).name }}
            </div>
          </div>
        </div>
      </vddl-draggable>
      <!-- More results button -->
      <button
        v-if="!loadingResults && hasMore"
        class="more"
        @click="onClickMoreResults"
      >
        <span class="text">Show all results</span>
      </button>
      <button
        v-else-if="loadingResults"
        class="more"
        @click="onClickMoreResults"
      >
        <svg-icon
          class="loading"
          :width="'22'"
          :height="'22'"
          icon="app-icons/spinner"
        />
      </button>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import emitter from 'tiny-emitter/instance';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS, EVENT, DRAG_TYPE, DRAG_LIST } from '@/constants.js';
import SvgIcon from './SvgIcon.vue';
import {
  loadImage,
  has,
  stripHTML,
  matchesHTML,
  findString,
  markString,
  getTextContent,
  truncateString,
} from '../../utils.js';
import { fittingString, round2DContextPath } from '../../canvas-utils.js';

export default {
  components: {
    SvgIcon,
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      search: null,
      elementType: DRAG_TYPE.SEARCH_ELEMENT,
      componentType: DRAG_TYPE.SEARCH_COMPONENT,
      componentAssetImage: {},
      filteredElements: [],
      filteredNotes: [],
      filteredJumpers: [],
      filteredBranches: [],
      filteredConnections: [],
      filteredBoards: [],
      filteredComponents: [],
      loadingResults: false, // State of loading more results
      searching: false, // State of searching initial results
      hasMore: false, // We have loaded initial results but there are more to load if requested
      performSearchDebounced: () => {},
    };
  },
  computed: {
    ...mapGetters('project/data/boards', {
      getAllBoards: 'getAllNodes',
      getBoardRootContainer: 'getRootContainer',
    }),
    ...mapGetters('project/data/elements', [
      'getAllNodes',
      'getContainerNode',
      'getNode',
      'getBoundingRect',
    ]),
    ...mapGetters('project/data/jumpers', {
      getAllJumperNodes: 'getAllNodes',
      getJumperContainerNode: 'getContainerNode',
      getJumperNode: 'getNode',
    }),
    ...mapGetters('project/data/branches', [
      'getBranchWithCondition',
      'getConditions',
    ]),
    ...mapGetters('project/data/branches', {
      getBranchNode: 'getNode',
      getAllBranchNodes: 'getAllNodes',
      getBranchContainerNode: 'getContainerNode',
    }),
    ...mapGetters('project/data/notes', {
      getAllNoteNodes: 'getAllNodes',
      getNoteContainerNode: 'getContainerNode',
    }),
    ...mapGetters('project/data/connections', {
      getAllConnectionNodes: 'getAllNodes',
      getConnectionContainerNode: 'getContainerNode',
    }),
    ...mapGetters('project/data/conditions', { getConditionNode: 'getNode' }),
    ...mapGetters('project/data/components', {
      getAllComponentNodes: 'getAllNodes',
      getComponentContainerNode: 'getContainerNode',
      getRootContainer: 'getRootContainer',
      getImageUrl: 'getImageUrl',
      getCoverIcon: 'getCoverIcon',
      getDefaultIcon: 'getDefaultIcon',
      getCoverId: 'getCoverId',
      getContainedNodes: 'getContainedNodes',
      getComponentNode: 'getNode',
    }),
    ...mapGetters('project/data/assets', ['getTinyImageUrl']),
    ...mapGetters('application', ['getSearchSection']),
    ...mapGetters('application/drag', ['getItems', 'getSource', 'getTarget']),

    /**
     * Returns whether the search has any results
     * @returns {Boolean}
     */
    resultCount() {
      return (
        this.filteredElements.length +
        this.filteredNotes.length +
        this.filteredJumpers.length +
        this.filteredBranches.length +
        this.filteredConnections.length +
        this.filteredBoards.length +
        this.filteredComponents.length
      );
    },
  },

  watch: {
    /**
     * Watch search section visibility change.
     */
    getSearchSection(newValue, oldValue) {
      if (newValue === true) {
        this.$refs.searchInput.focus();
      }
    },

    /**
     * Watcher for a change in the search term. Initiates a (debounced) search
     */
    async search(newValue, oldValue) {
      this.searching = true;
      this.resetFilterResults();
      this.performSearchDebounced(newValue);
    },
  },

  created() {
    // Debounce search so that it is triggered at most every 300 msec
    this.performSearchDebounced = _.debounce(this.performSearch, 300);
  },

  updated() {
    // If we are already showing more results then we are not loading them
    if (this.resultCount > 20) {
      this.loadingResults = false;
    }
  },

  methods: {
    ...mapActions('project/config/components', ['setOpen']),
    ...mapActions('project/config/boards', {
      setBoardOpen: 'setOpen',
    }),
    ...mapActions('application/drag', [
      'setItems',
      'setSource',
      'setTarget',
      'clear',
    ]),
    ...mapActions('project/data/jumpers', {
      updateJumper: 'customUpdate',
    }),
    ...mapActions('project/data/elements', {
      addElementComponent: 'addComponent',
    }),
    ...mapActions('project/data/attributes', {
      addAttributeComponent: 'addComponent',
    }),

    /**
     * Start a project search. Debounced method.
     * @param {string} pattern The pattern to use for the search
     * @param {number} limit How many results to display
     */
    performSearch(pattern, limit = 20) {
      this.hasMore = false;
      if (!limit) {
        this.filteredElements = this.filterElements(pattern);
        this.filteredJumpers = this.filterJumpers(pattern);
        this.filteredNotes = this.filterNotes(pattern);
        this.filteredBranches = this.filterBranches(pattern);
        this.filteredConnections = this.filterConnections(pattern);
        this.filteredBoards = this.filterBoards(pattern);
        this.filteredComponents = this.filterComponents(pattern);
      } else {
        this.resetFilterResults();
        /**
         * Filter the project data using the specified filter function and limits
         * @param {number} itemLimit How many results are we allowed to return for this item
         * @param {boolean} more Have we exceeded the limit and got more results
         * @param {function} filterFunction A callback to execute for filtering the specific item type
         * @returns {array} An array containing how many items are remaining, whether we have exceeded our limit and the items we got
         */
        const filterItems = (itemLimit, more, filterFunction) => {
          // In case we got enough results already do not search further
          if (more) return [0, more, []];

          // Retrieve all the results for the specific board items
          const filtered = filterFunction(pattern);

          // Determine if we did get more results than our limit
          more = more || filtered.length > itemLimit;

          // Get the part of the results that does not exceed our limit
          const items = filtered.slice(0, itemLimit);

          // How many more items we need to fetch before reaching our total limit
          itemLimit -= items.length;
          return [itemLimit, more, items];
        };
        // Our item limit starts from total limit and can go down to 0
        let itemLimit = limit;
        [
          [this.filteredElements, this.filterElements],
          [this.filteredJumpers, this.filterJumpers],
          [this.filteredNotes, this.filterNotes],
          [this.filteredBranches, this.filterBranches],
          [this.filteredConnections, this.filterConnections],
          [this.filteredBoards, this.filterBoards],
          [this.filteredComponents, this.filterComponents],
        ].forEach(([prop, func]) => {
          let items;
          [itemLimit, this.hasMore, items] = filterItems(
            itemLimit,
            this.hasMore,
            func
          );
          prop.push(...items);
        });
      }
      this.searching = false;
    },

    /**
     * Empty all the filter results to initiate a new search
     */
    resetFilterResults() {
      this.filteredElements = [];
      this.filteredNotes = [];
      this.filteredJumpers = [];
      this.filteredBranches = [];
      this.filteredConnections = [];
      this.filteredBoards = [];
      this.filteredComponents = [];
    },

    /**
     * Event handler: User clicks to see more search results. Initiate a non-debounced search.
     */
    onClickMoreResults() {
      this.loadingResults = true;
      this.performSearch(this.search, null);
    },

    /**
     * Returns the boards that are matched by the search term
     * @param {string} pattern The search pattern
     * @returns {Array}
     */
    filterBoards(pattern) {
      return this.getAllBoards.filter(board => {
        if (!pattern) return false;
        const { name } = board;
        return !has(board, 'children') && matchesHTML(pattern, name);
      });
    },

    /**
     * Returns the elements that are matched by the search term
     * @param {string} pattern The search pattern
     * @returns {Array}
     */
    filterElements(pattern) {
      return this.getAllNodes.filter(element => {
        if (!pattern) return false;
        const { title, content } = element;
        return (
          (title && matchesHTML(pattern, title)) ||
          (content && matchesHTML(pattern, content))
        );
      });
    },

    /**
     * Returns the νοτεs that are matched by the search term
     * @param {string} pattern The search pattern
     * @returns {Array}
     */
    filterJumpers(pattern) {
      return this.getAllJumperNodes.filter(jumper => {
        if (!pattern) return false;
        const { elementId } = jumper;
        if (!elementId) return false;
        const { title } = this.getNode(elementId);
        return title && matchesHTML(pattern, title);
      });
    },

    /**
     * Returns the notes that are matched by the search term
     * @param {string} pattern The search pattern
     * @returns {Array}
     */
    filterNotes(pattern) {
      return this.getAllNoteNodes.filter(note => {
        if (!pattern) return false;
        const { content } = note;
        return content && matchesHTML(pattern, content);
      });
    },

    /**
     * Returns the branches that have at least one condition matched by the search term
     * @param {string} pattern The search pattern
     * @returns {Array}
     */
    filterBranches(pattern) {
      return this.getAllBranchNodes.filter(branch => {
        if (!pattern) return false;
        const conditions = this.getConditions(branch.id);
        return conditions.some(conditionId => {
          const { script } = this.getConditionNode(conditionId);
          return script && matchesHTML(pattern, script);
        });
      });
    },

    /**
     * Returns the connections that are matched by the search term
     * @param {string} pattern The search pattern
     * @returns {Array}
     */
    filterConnections(pattern) {
      return this.getAllConnectionNodes.filter(connection => {
        if (!pattern) return false;
        const { label } = connection;
        return label && matchesHTML(pattern, label);
      });
    },

    /**
     * Returns the components that are matched by the search term
     * @param {string} pattern The search pattern
     * @returns {Array}
     */
    filterComponents(pattern) {
      return this.getAllComponentNodes.filter(component => {
        if (!pattern || component.children) return false;
        const { name, id } = component;
        const attributes = this.getContainedNodes(
          id,
          'attributes',
          'attributes'
        );
        const res = attributes.some(
          attribute =>
            matchesHTML(pattern, attribute.name) ||
            (attribute.value.type === 'string' &&
              matchesHTML(pattern, attribute.value.data))
        );
        if (res) return true;
        return name && matchesHTML(pattern, name);
      });
    },

    onClickBoard(board) {
      // Open this board
      this.setBoardOpen({
        id: board.id,
        apply: true,
      });
    },
    /**
     * Event handler for clicking an element that matches the search.
     * @param   {Object}  element
     */
    onClickElement(element) {
      const { id } = element;
      const board = this.getContainerNode(id, 'boards', 'elements');
      emitter.emit(EVENT.ZOOMTONODE, {
        node: element,
        type: 'elements',
        boardId: board.id,
      });
    },

    /**
     * Event handler for clicking a note that matches the search.
     * @param   {Object}  note
     */
    onClickNote(note) {
      const { id } = note;
      const board = this.getNoteContainerNode(id, 'boards', 'notes');
      emitter.emit(EVENT.ZOOMTONODE, {
        node: note,
        type: 'notes',
        boardId: board.id,
      });
    },

    /**
     * Event handler for clicking a jumper that matches the search.
     * @param   {Object}  note
     */
    onClickJumper(jumper) {
      const { id } = jumper;
      const board = this.getJumperContainerNode(id, 'boards', 'jumpers');
      emitter.emit(EVENT.ZOOMTONODE, {
        node: jumper,
        type: 'jumpers',
        boardId: board.id,
      });
    },

    /**
     * Event handler for clicking a branch that matches the search.
     * @param   {Object}  branch
     */
    onClickBranch(branch) {
      const { id } = branch;
      const board = this.getBranchContainerNode(id, 'boards', 'branches');
      emitter.emit(EVENT.ZOOMTONODE, {
        node: branch,
        type: 'branches',
        boardId: board.id,
      });
    },

    /**
     * Event handler for clicking on a component that matches the search.
     * @param   {Object}  component
     */
    onClickComponent(component) {
      this.setOpen({ id: component.id, apply: true });
    },

    /**
     * Event handler for clicking on a connection that matches the search
     * @param   {Object}  connection
     */
    onClickConnection(connection) {
      const { id, sourceid, targetid, sourceType, targetType } = connection;
      let sourceNode = null;
      if (sourceType === 'conditions') {
        // condition
        sourceNode = this.getBranchWithCondition(sourceid);
        sourceNode.height = 41;
        sourceNode.width = 300;
      } else {
        // element
        sourceNode = this.getNode(sourceid);
      }
      let targetNode = null;
      if (targetType === 'jumpers') {
        // jumper
        targetNode = this.getJumperNode(targetid);
        targetNode.height = 81;
        targetNode.width = 200;
      } else if (targetType === 'branches') {
        // branch
        targetNode = this.getBranchNode(targetid);
        targetNode.height = 41;
        targetNode.width = 300;
      } else {
        // element
        targetNode = this.getNode(targetid);
      }
      const nodes = [sourceNode, targetNode];
      const rect = this.getBoundingRect(nodes);
      const board = this.getConnectionContainerNode(
        id,
        'boards',
        'connections'
      );
      // Zoom to the bounding rectangle of source, target elements
      emitter.emit(EVENT.ZOOMTOBOARD, {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
        boardId: board.id,
      });
    },

    /**
     * Return the title from the element, stripped from HTML tags.
     * @param   {Object}  element
     * @return  {String}
     */
    boardTitle(board) {
      const { name } = board;
      if (!stripHTML(name)) return 'Untitled board';
      const result = findString(stripHTML(name), this.search, 30);
      return markString(result, this.search);
    },

    /**
     * Return the title from the element, stripped from HTML tags.
     * @param   {Object}  element
     * @return  {String}
     */
    elementTitle(element) {
      const { title } = element;
      if (!stripHTML(title)) return 'Untitled element';
      const result = findString(stripHTML(title), this.search, 30);
      return markString(result, this.search);
    },

    /**
     * Return the content from the element, stripped from HTML tags
     * @param   {Object}  element
     * @returns {String}
     */
    elementContent(element) {
      const { content } = element;
      const result = findString(stripHTML(content), this.search, 30, true);
      return markString(result, this.search) || null;
    },

    /**
     * Return the title of the jumper (element title), stripped from HTML tags.
     * @param   {Object}  element
     * @return  {String}
     */
    jumperTitle(jumper) {
      const { elementId } = jumper;
      if (!elementId) return null;
      const { title } = this.getNode(elementId);
      const result = findString(stripHTML(title), this.search, 30, true);
      return markString(result, this.search);
    },

    /**
     * Return the content from the note, stripped from HTML tags
     * @param   {Object}  note
     * @returns {String}
     */
    noteContent(note) {
      const { content } = note;
      const result = findString(stripHTML(content), this.search, 30);
      return markString(result, this.search);
    },

    /**
     * Return the content from the branch, stripped from HTML tags
     * @param   {Object}  branch
     * @returns {String}
     */
    branchContent(branch) {
      const conditions = this.getConditions(branch.id);
      // Find the first matching condition
      const condition = conditions.find(conditionId => {
        const { script } = this.getConditionNode(conditionId);
        return script && matchesHTML(this.search, script);
      });
      const { script } = this.getConditionNode(condition);
      const result = findString(stripHTML(script), this.search, 30);
      return markString(result, this.search);
    },

    /**
     * Return the label from the connection, stripped from HTML tags
     * @param   {Object}  connection
     * @returns {String}
     */
    connectionLabel(connection) {
      const { label } = connection;
      const result = findString(stripHTML(label), this.search, 30);
      return markString(result, this.search);
    },

    /**
     * Return the name from the component
     * @param   {Object}  component
     * @returns {String}
     */
    componentName(component) {
      const { name } = component;
      const result = findString(stripHTML(name), this.search, 30);
      return markString(result, this.search);
    },

    /**
     * Return the content for the component
     * @param   {Object}  component
     * @returns {String}
     */
    componentContent(component) {
      const attributes = this.getContainedNodes(
        component.id,
        'attributes',
        'attributes'
      );
      const foundAttribute = attributes.find(attribute => {
        const { name, value } = attribute;
        return (
          (name && name.toLowerCase().includes(this.search.toLowerCase())) ||
          (value.type === 'string' &&
            value.data &&
            value.data.toLowerCase().includes(this.search.toLowerCase()))
        );
      });
      if (foundAttribute) {
        const { name, value } = foundAttribute;
        if (name && name.toLowerCase().includes(this.search.toLowerCase())) {
          return markString(
            findString(stripHTML(name), this.search, 30),
            this.search
          );
        }
        if (
          value.type === 'string' &&
          value.data &&
          value.data.toLowerCase().includes(this.search.toLowerCase())
        ) {
          return markString(
            findString(stripHTML(value.data), this.search, 30),
            this.search
          );
        }
      }
      return null;
    },

    /**
     * Return the parent container for the component
     * @param   {Object}  component
     * @returns {Object}
     */
    componentParent(component) {
      return this.getComponentContainerNode(
        component.id,
        'components',
        'children'
      );
    },

    /**
     * Return the board an element is in
     * @param   {Object}  element
     * @returns {Object}
     */
    elementParent(element) {
      return this.getContainerNode(element.id, 'boards', 'elements');
    },

    /**
     * Return the board a jumper is in
     * @param   {Object}  jumper
     * @returns {Object}
     */
    jumperParent(jumper) {
      return this.getContainerNode(jumper.id, 'boards', 'jumpers');
    },

    /**
     * Return the board a branch is in
     * @param   {Object}  branch
     * @returns {Object}
     */
    branchParent(branch) {
      return this.getContainerNode(branch.id, 'boards', 'branches');
    },

    /**
     * Return the board a note is in
     * @param   {Object}  note
     * @returns {Object}
     */
    noteParent(note) {
      return this.getContainerNode(note.id, 'boards', 'notes');
    },

    /**
     * Return the board the connection is in
     * @param   {Object}  connection
     * @returns {Object}
     */
    connectionParent(connection) {
      return this.getConnectionContainerNode(
        connection.id,
        'boards',
        'connections'
      );
    },

    /**
     * The tiny image url for a component cover asset
     * @returns {String}
     */
    componentImageUrl(component) {
      const coverId = this.getCoverId(component.id);
      return this.getTinyImageUrl(coverId);
    },

    /**
     * The cover icon for a component
     * @returns {String}
     */
    icon(component) {
      return this.getCoverIcon(component.id) || this.getDefaultIcon;
    },

    /**
     * Event handler for vddl dragstart event. The element item has started dragging.
     */
    onElementDragStart(draggable) {
      // Add class to body for hover fix
      document.body.classList.add('dragging');

      this.setItems({
        value: {
          type: this.elementType,
          ids: [draggable.dataset.id],
        },
      });
      this.setSource({
        value: {
          type: DRAG_LIST.SEARCH,
          ids: [],
          indexes: [],
        },
      });
    },

    /**
     * Event handler for vddl dragstart event. The element item has started dragging.
     */
    onComponentDragStart(draggable) {
      // Add class to body for hover fix
      document.body.classList.add('dragging');

      this.setItems({
        value: {
          type: this.componentType,
          ids: [draggable.dataset.id],
        },
      });
      this.setSource({
        value: {
          type: DRAG_LIST.SEARCH,
          ids: [],
          indexes: [],
        },
      });
    },

    /**
     * Custom event for svg icon load for the element icon
     */
    async onElementIconLoad(svg) {
      const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
      this.elementAssetImage = await loadImage(url);
    },

    /**
     * Custom event for svg icon load for the component icon
     */
    async onComponentIconLoad(svg, id) {
      const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
      this.componentAssetImage[id] = await loadImage(url);
    },

    /**
     * Customize the drag image for the dragged element
     */
    onStartDrag(event) {
      if (this.canEdit) {
        // Find the text to display
        const { id } = event.target.dataset;
        const { title, content } = this.getNode(id);
        const result = getTextContent(title) || getTextContent(content);
        const name = truncateString(result, 100) || 'Empty element';

        const canvas = document.createElement('canvas');
        canvas.classList.add('dragCanvasSidebar');
        canvas.width = 200;
        canvas.height = 32;

        // This is required in Chrome (check styles below)
        document.body.append(canvas);
        const ctx = canvas.getContext('2d');

        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(this.elementAssetImage, 14, 8, 16, 16);

        // Draw text
        ctx.font = '12px "Open Sans",sans-serif';
        ctx.fillStyle = 'white';
        ctx.fillText(fittingString(ctx, name, 150), 40, 20);

        event.dataTransfer.setDragImage(
          canvas,
          canvas.width / 2,
          canvas.height / 2
        );
      }
    },

    /**
     * Component image onLoad event
     */
    onComponentImageLoad(url, id) {
      this.componentAssetImage[id] = new Image();
      this.componentAssetImage[id].src = url;
    },

    /**
     * Customize drag image for the dragged component
     */
    onComponentStartDrag(event) {
      if (!this.canEdit) return;

      // Find the text to display
      const { id } = event.target.dataset;
      const { name } = this.getComponentNode(id);

      const canvas = document.createElement('canvas');
      canvas.classList.add('dragCanvasSidebar');
      canvas.width = 200;
      canvas.height = 32;

      // This is required in Chrome (check styles below)
      document.body.append(canvas);
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (this.getCoverId(id)) {
        const image = this.componentAssetImage[id];

        // Compute the minimum dimension in order to crop the image
        const minDimension =
          image.width < image.height ? image.width : image.height;

        // Offsets for image cropping
        const sx = (image.width - minDimension) / 2;
        const sy = (image.height - minDimension) / 2;

        // Save current canvas state as we will begin masking the image
        ctx.save();

        // Create the mask
        round2DContextPath(ctx, 8, 5, 22, 22, 11);
        ctx.clip();
        // Draw the image on the canvas
        ctx.drawImage(image, sx, sy, minDimension, minDimension, 8, 5, 22, 22);

        // Restore canvas
        ctx.restore();
      } else if (this.getCoverIcon(id)) {
        if (this.icon === 'app-icons/folder') {
          ctx.drawImage(this.componentAssetImage[id], 12, 6, 16, 16);
        } else {
          ctx.drawImage(this.componentAssetImage[id], 14, 6, 18, 18);
        }
      }

      // Draw text
      ctx.font = '12px "Open Sans",sans-serif';
      ctx.fillStyle = 'white';
      ctx.fillText(fittingString(ctx, name, 150), 40, 20);

      event.dataTransfer.setDragImage(
        canvas,
        canvas.width / 2,
        canvas.height / 2
      );
    },

    /**
     * Event handler for vddl dragend event.
     */
    onDragEnd(event) {
      // Remove class from body for hover fix
      document.body.classList.remove('dragging');

      // Remove any drag canvas
      $('.dragCanvasSidebar').remove();
    },

    /**
     * Event handler for vddl moved event. The item has been moved to a correct position.
     */
    onMove(event) {
      console.log('*******ONMOVED', event);
      const items = this.getItems;
      const source = this.getSource;
      const target = this.getTarget;
      // In case it was dropped on a jumper
      if (target.type === DRAG_LIST.JUMPERS) {
        this.updateJumper({
          id: target.id,
          props: { elementId: items.ids[0] },
          apply: true,
        });
      }
      // Clear drag information
      this.clear();
    },

    /**
     * Event handler for vddl moved event.A component been moved to a correct position.
     */
    onComponentMove(event) {
      const items = this.getItems;
      const source = this.getSource;
      const target = this.getTarget;
      console.log('onMove called. Target:', target);
      // In case it was dropped on the component tree perform move
      if (target.type === DRAG_LIST.ELEMENTS) {
        this.addElementComponent({
          id: target.id,
          componentId: items.ids[0],
          index: target.index,
          apply: true,
        });
      } else if (target.type === DRAG_LIST.ATTRIBUTES) {
        this.addAttributeComponent({
          id: target.id,
          componentId: items.ids[0],
          index: target.index,
          apply: true,
        });
      }
      // Clear drag information
      this.clear();
    },

    /**
     * Event handler for vddl canceled event.
     */
    onCancel(event) {
      this.clear();
    },
  },
};
</script>

<style scoped lang="scss">
.search_sidebar {
  width: 247px;
  background: #2c3233;
  z-index: 2;
  box-shadow: -2px 3px 6px 0 rgba(0, 0, 0, 0.2);
  &.no_results {
    position: absolute;
    right: 0;
    z-index: 100;
  }
  .results_msg {
    color: #bcbcbc;
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    margin: 4px 0 12px 0;
    opacity: 0.8;
  }
  .results {
    height: calc(100% - 55px);
    .header {
      text-transform: uppercase;
      color: #bcbcbc;
      font-weight: 400;
      font-size: 12px;
      opacity: 0.5;
      padding: 0 10px;
      margin: 10px 0;
    }
    .item {
      display: flex;
      align-items: flex-start;
      justify-content: left;
      min-width: 30px;
      min-height: 30px;
      width: 100%;
      padding: 8px 10px 7px 10px;
      cursor: pointer;
      user-select: none;
      &:hover {
        background: #363e3f;
      }
      .icon {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 16px;
        max-width: 16px;
        margin: 2px 9px 0 0;
      }
      .thumb {
        width: 22px;
        height: 22px;
        min-width: 22px;
        min-height: 22px;
        overflow: hidden;
        margin: 0 6px 0 -2px;
        display: flex;
        align-items: center;
        justify-content: center;
        &.hasimage {
          border-radius: 999px;
        }
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          user-select: none;
          -webkit-user-drag: none;
        }
      }
      .details {
        flex: 1;
        display: flex;
        flex-flow: column;
        width: calc(100% - 15px);
        .name {
          display: block;
          color: #bcbcbc;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 14px;
          font-weight: 600;
          margin: 0 0 4px 0;
          flex: 1;

          :deep(span) {
            color: #fff;
          }
        }
        .content {
          color: #bcbcbc;
          flex: 1;
          margin: 0 0 3px 0;
          // font-style: italic;
          :deep(span) {
            color: #fff;
          }
        }
        .parent {
          flex: 0 0 100%;
          color: #bcbcbc;
          font-size: 12px;
          opacity: 0.7;
        }
      }
    }

    button.more {
      background: #383f40;
      color: #bcbcbc;
      min-width: 100%;
      border: none;
      border-radius: 0;
      min-height: 34px;
      padding: 8px 12px 8px 76px;
      text-align: left;
      cursor: pointer;
      user-select: none;
      font-family: 'Open Sans', sans-serif;
      display: flex;
      position: relative;
      &:hover {
        background: #3e4748;
        color: #fff;
      }
      .loading {
        position: absolute;
        left: 110px;
      }
    }
  }
  .input_container {
    margin: 10px;
    position: relative;
    .right_icon {
      .clear {
        position: absolute;
        opacity: 0.25;
        top: 12px;
        right: 12px;
        cursor: pointer;
        &:hover {
          opacity: 0.8;
        }
      }
      .loading {
        position: absolute;
        top: 12px;
        right: 12px;
        opacity: 0.5;
      }
    }
    .search_input {
      background: #0d0e0e;
      width: 100%;
      padding: 10px;
      border: none;
      border-radius: 4px;
      color: #bcbcbc;
    }
  }
}

// SIDEBAR SCROLLBAR STYLES

.search_sidebar .results {
  scrollbar-color: rgba(0, 0, 0, 0.5) transparent;
  scrollbar-width: thin;
  overflow: hidden;
  @media (hover: none) {
    overflow-y: auto;
  }
  &:hover {
    overflow-y: auto;
    overflow: overlay;
  }
}

.search_sidebar .results::-webkit-scrollbar {
  width: 10px;
}

.search_sidebar .results::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.5);
  border: 2px solid transparent;
  border-radius: 999px;
  background-clip: padding-box;
}

.search_sidebar .results::-webkit-scrollbar-button {
  display: none;
}
</style>
